import React from 'react';
import { useNarrowScreen } from '../hooks/useMediaQuery';
import { fullName, shortName, shortNameFromFull } from '../utils/names';

type PlayerNameProps = {
  className?: string;
  style?: React.CSSProperties;
} & (
  | { first?: string | null; last?: string | null; full?: never }
  | { full: string | null | undefined; first?: never; last?: never }
);

/**
 * A player's name for a table cell: the full name normally, "N. Destounis"
 * on narrow screens (≤ 520px) so name columns stop pushing numeric columns off
 * the phone. The full name is always available via `title` / `aria-label`.
 *
 * Display only — callers keep using the full name for keys and lookups.
 */
const PlayerName: React.FC<PlayerNameProps> = ({ className, style, ...props }) => {
  const narrow = useNarrowScreen();
  const long = 'full' in props && props.full !== undefined
    ? String(props.full ?? '').trim()
    : fullName(props.first, props.last);
  const short = 'full' in props && props.full !== undefined
    ? shortNameFromFull(props.full)
    : shortName(props.first, props.last);
  const text = narrow ? short : long;
  const abbreviated = narrow && short !== long;
  return (
    <span
      className={className}
      style={style}
      title={abbreviated ? long : undefined}
      aria-label={abbreviated ? long : undefined}
    >
      {text}
    </span>
  );
};

export default PlayerName;
